/**
 * An agent's `desktop_pin` (pin a frame to the user's desktop, or unpin
 * it), carried out by the owner overlay exactly like the user's own Pin to
 * desktop. The backend checked that the frame is the user's, in the agent's
 * project, and named its type; the agent confirms through the screen report
 * (`focus-state-report.ts`). A frame already pinned stays where it is.
 */
import type { FrameType } from "@/api/spaces";
import type { WsMessage } from "@/api/websocket";
import { isTauri } from "@/lib/is-tauri";
import { isRemoteDeviceAction } from "@/lib/remote-device-frame";
import { pinToDesktopForAgent, unpinFromDesktop } from "./desktop-pin-actions";
import { isWidgetSize } from "./desktop-pin-resize";
import { desktopPinFor } from "./desktop-pins-store";

export const DESKTOP_PIN_REQUEST = "desktop.pin_request";

const text = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

/** Called for every owner socket message; true when it was an agent's pin request. */
export function applyAgentDesktopPinRequest(msg: WsMessage): boolean {
  if (msg.type !== DESKTOP_PIN_REQUEST) return false;
  if (!isTauri() || isRemoteDeviceAction(msg)) return true;
  const { data } = msg;
  const frameId = text(data.frame_id);
  const projectId = text(data.project_id);
  const frameType = text(data.frame_type);
  if (!frameId || !projectId) return true;
  if (data.operation === "unpin") {
    unpinFromDesktop(frameId);
    return true;
  }
  if (data.operation !== "pin" || !frameType || desktopPinFor(frameId))
    return true;
  pinToDesktopForAgent({
    frameId,
    projectId,
    frameType: frameType as FrameType,
    displayId: typeof data.display_id === "number" ? data.display_id : null,
    size: isWidgetSize(data.size) ? data.size : null,
  });
  return true;
}
